<template>
  <div>
    <v-btn v-if="!hideButton" :elevation="0" @click="open()">
      {{ $t("create") }}
    </v-btn>

    <confirm-action-dialog
      v-if="isDialogActive"
      :width="width"
      height="78vh"
      :persistent="true"
      :supressKeyboardActions="true"
      :title="$t('template.createTemplate')"
      :isDialogActive.sync="isDialogActive"
      :rightText="$t('create')"
      :loading="isLoading"
      :fullscreen="isFullscreen"
      :rightLoading="isLoading"
      :rightDisabled="!isFormValid || !template.content.body || !template.content.subject"
      @leftClick="close"
      @close="close"
      @rightClick="onRightClick"
    >
      <template #actions>
        <tooltip
          :text="isFullscreen ? $t('template.createDialog.exitFullscreen') : $t('template.createDialog.fullscreen')"
        >
          <v-btn icon @click="isFullscreen = !isFullscreen">
            <v-icon>{{ isFullscreen ? "mdi-fullscreen-exit" : "mdi-fullscreen" }}</v-icon>
          </v-btn>
        </tooltip>
      </template>

      <v-form v-model="isFormValid">
        <!-- title & key -->
        <v-row class="pt-3" dense>
          <v-col cols="12" :md="isKeyVisible ? 7 : 12">
            <v-text-field
              v-model="template.meta.title"
              :label="$t('objects.template.meta.title')"
              :hint="$t('objects.template.meta.titleHint')"
              :rules="rules(RulesEnum.REQUIRED_RULE)"
              ref="startField"
              persistent-hint
              outlined
            >
              <template v-if="!isKeyAlwaysVisible" #append-outer>
                <tooltip
                  :text="isKeyVisible ? $t('template.createDialog.hideKey') : $t('template.createDialog.showKey')"
                >
                  <v-btn icon small @click="toggleKeyInput">
                    <v-icon small>{{ isKeyVisible ? "mdi-key-remove" : "mdi-key-variant" }}</v-icon>
                  </v-btn>
                </tooltip>
              </template>
            </v-text-field>
          </v-col>

          <v-col v-if="isKeyVisible" cols="12" md="5">
            <v-text-field
              :value="template.key"
              :label="$t('objects.template.key')"
              :hint="$t('objects.template.keyHint')"
              :rules="keyRules"
              persistent-hint
              outlined
              @input="onKeyInput"
            />
          </v-col>
        </v-row>

        <!-- language & content format -->
        <v-row dense>
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="template.meta.language"
              :label="$t('objects.template.meta.language')"
              :hint="$t('objects.template.meta.languageHint')"
              outlined
              persistent-hint
              :items="availableLanguages"
              :rules="rules()"
              :filter="getAutocompleteFilter((e) => [$t(`enums.LanguageCodeEnum.${e}`)])"
            >
              <template #item="{ item }">
                {{ item | getFlagEmojiByLanguage }} {{ $t(`enums.LanguageCodeEnum.${item}`) }}
              </template>
              <template #selection="{ item }">
                {{ item | getFlagEmojiByLanguage }} {{ $t(`enums.LanguageCodeEnum.${item}`) }}
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" md="6">
            <v-select
              :value="isPlain"
              :label="$t('objects.template.meta.contentFormat')"
              :hint="$t('objects.template.meta.contentFormatHint')"
              persistent-hint
              outlined
              :items="[true, false]"
              @change="onContentFormatChange"
            >
              <template #item="{ item }">
                <span v-if="item">
                  {{ $t("enums.MessageContentFormatEnum.plain") }}
                </span>
                <span v-else>
                  {{ $t("enums.MessageContentFormatEnum.html") }}
                </span>
              </template>
              <template #selection="{ item }">
                <span v-if="item">
                  {{ $t("enums.MessageContentFormatEnum.plain") }}
                </span>
                <span v-else>
                  {{ $t("enums.MessageContentFormatEnum.html") }}
                </span>
              </template>
            </v-select>
          </v-col>
        </v-row>

        <!-- subject -->
        <v-row dense>
          <v-col cols="12">
            <template-editor-subject
              :partnerId="partnerId"
              :subject.sync="template.content.subject"
              :addPlaceholderInSubject="true"
              :rules="rules(RulesEnum.REQUIRED_RULE)"
            />
          </v-col>
        </v-row>

        <!-- body -->
        <v-row dense>
          <v-col cols="12">
            <div v-if="isEditorModeSelectable" class="d-flex align-center flex-wrap mb-2">
              <v-btn-toggle v-model="editorMode" mandatory dense>
                <v-btn small :value="TemplateBodyEditorModeEnum.RICH">
                  <v-icon small left> mdi-format-text </v-icon>
                  {{ $t("components.template.editor.mode.rich") }}
                </v-btn>
                <v-btn small :value="TemplateBodyEditorModeEnum.HTML">
                  <v-icon small left> mdi-code-tags </v-icon>
                  {{ $t("components.template.editor.mode.html") }}
                </v-btn>
              </v-btn-toggle>
              <small class="ml-3 grey--text">{{ editorModeHint }}</small>
            </div>

            <v-textarea
              v-if="isPlain"
              v-model="template.content.body"
              outlined
              auto-grow
              :label="$t('components.template.detail.body.body')"
            >
              <template #append>
                <template-editor-add-placeholder
                  @addPlaceholder="template.content.body += $event"
                  :partnerId="partnerId"
                  class="mt-n2"
                />
              </template>
            </v-textarea>

            <template-editor-html-source
              v-else-if="isHtmlSource"
              v-model="template.content.body"
              :partnerId="partnerId"
              :height="bodyHeight"
            />

            <template-editor
              v-else
              v-model="template.content.body"
              :key="'template-editor' + isPlain"
              :isPlain="isPlain"
              :readOnly="false"
              :parsePastedHtml="true"
              :normalizeOnMount="true"
              :editConfiguration="editConfiguration"
              :partnerId="partnerId"
              :label="$t('components.template.detail.body.body')"
            />
          </v-col>
        </v-row>

        <!-- visibility for partners -->
        <v-row v-if="!partnerId" dense class="mb-1">
          <v-col cols="12" class="d-flex align-center">
            <v-checkbox
              v-model="template.isPublic"
              class="mt-0 pt-0"
              dense
              hide-details
              :label="$t('template.createDialog.isPublicLabel')"
              data-test-is-public
            />
            <tooltip :text="$t('objects.template.isPublicHint')">
              <v-icon small class="ml-1"> mdi-information-outline </v-icon>
            </tooltip>
          </v-col>
        </v-row>

        <debug>
          {{ template }}
        </debug>
      </v-form>
    </confirm-action-dialog>
  </div>
</template>

<script lang="ts">
import TemplateEditor, { TemplateEditConfiguration } from "@/components/template/TemplateEditor.vue";
import Debug from "@/components/utility/Debug.vue";
import { LanguageCodeEnum } from "@/lib/enum/language-code.enum";
import { MessageContentFormatEnum } from "@/lib/enum/templateEnums/messageContentFormat.enum";
import { TemplateBodyEditorModeEnum } from "@/lib/enum/templateEnums/templateBodyEditorMode.enum";
import { TemplateEditorEnum } from "@/lib/enum/templateEnums/tempalteEditor.enum";
import { IDialog } from "@/lib/interfaces/dialog.interface";
import { templateKeyRule } from "@/lib/rules/keyRule";
import { requiredRule } from "@/lib/rules/requiredRule";
import { simpleDoubleDigitDate } from "@/lib/utility/date-helper";
import { getAutocompleteFilter } from "@/lib/utility/filter";
import { handleError } from "@/lib/utility/handleError";
import PartnerFallbackMixin from "@/mixins/PartnerFallbackMixin.vue";
import RulesMixin from "@/mixins/RulesMixin.vue";
import { IReference } from "@/models/reference.entity";
import { buildTemplate, ITemplate } from "@/models/template.entity";
import { TemplateMeta } from "@/models/template-meta.entity";
import { ConfigModule } from "@/store/modules/config";
import { mixins } from "vue-class-component";
import { Component, Prop } from "vue-property-decorator";
import ConfirmActionDialog from "../utility/ConfirmActionDialog.vue";
import Tooltip from "../utility/tooltip.vue";
import TemplateEditorAddPlaceholder from "./TemplateEditorAddPlaceholder.vue";
import TemplateEditorHtmlSource from "./TemplateEditorHtmlSource.vue";
import TemplateEditorSubject from "./TemplateEditorSubject.vue";
import { PartnerModule } from "@/store/modules/partner";
import { getFlagEmojiByLanguage } from "@/lib/CountryCodeHelper";

@Component({
  components: {
    ConfirmActionDialog,
    Debug,
    Tooltip,
    TemplateEditorSubject,
    TemplateEditor,
    TemplateEditorAddPlaceholder,
    TemplateEditorHtmlSource
  },
  filters: {
    simpleDoubleDigitDate,
    getFlagEmojiByLanguage
  }
})
export default class TemplateCreateDialog extends mixins(PartnerFallbackMixin, RulesMixin) implements IDialog {
  readonly getAutocompleteFilter = getAutocompleteFilter;

  readonly TemplateBodyEditorModeEnum = TemplateBodyEditorModeEnum;

  @Prop()
  partnerId!: string;

  @Prop()
  hideButton!: boolean;

  @Prop({ default: false })
  fullscreen?: boolean;

  @Prop({ default: "1400" })
  width!: string;

  @Prop({ default: () => [] })
  refs!: IReference[];

  isDialogActive = false;

  isLoading = false;

  isFormValid = false;

  isFullscreen = false;

  template: ITemplate = buildTemplate({
    partnerId: this.partnerId,
    meta: new TemplateMeta({
      title: "",
      editor: TemplateEditorEnum.TIPTAP,
      contentFormat: MessageContentFormatEnum.HTML,
      categories: [],
      language: LanguageCodeEnum.DE
    })
  });

  isPlain = false;

  editorMode = TemplateBodyEditorModeEnum.RICH;

  /**
   * Partners only see the key input after opting in, admins always configure the key explicitly.
   */
  isKeyInputRequested = false;

  get editConfiguration() {
    return new TemplateEditConfiguration(this.partnerId, true);
  }

  get availableLanguages() {
    return ConfigModule.availableLanguages;
  }

  /**
   * Admins configure the key of a template explicitly instead of letting the backend derive it
   * from the title. Partners can opt in, e.g. to create a specific key for an automation.
   */
  get isKeyAlwaysVisible() {
    return !this.partnerId;
  }

  get isKeyVisible() {
    return this.isKeyAlwaysVisible || this.isKeyInputRequested;
  }

  get keyRules() {
    const keyRules = [templateKeyRule()];

    // without an explicit key the backend derives one from the title, which admins should not rely on
    if (this.isKeyAlwaysVisible) keyRules.unshift(requiredRule());

    return keyRules;
  }

  /**
   * Editing raw html is reserved for admins. Partner templates go through the tiptap editor so
   * their html is parsed and unsupported markup is stripped.
   */
  get isEditorModeSelectable() {
    return !this.isPlain && !this.partnerId;
  }

  get isHtmlSource() {
    return this.isEditorModeSelectable && this.editorMode === TemplateBodyEditorModeEnum.HTML;
  }

  get editorModeHint() {
    return this.isHtmlSource
      ? this.$t("components.template.editor.mode.htmlHint")
      : this.$t("components.template.editor.mode.richHint");
  }

  get bodyHeight() {
    return this.isFullscreen ? 500 : 380;
  }

  /**
   * Opens the create dialog. Optionally preset the template key (e.g. when
   * creating a template for a known but not yet configured template key).
   */
  open(key?: string) {
    this.template = buildTemplate({
      partnerId: this.partnerId,
      key: typeof key === "string" ? key : undefined,
      meta: new TemplateMeta({
        title: "",
        editor: TemplateEditorEnum.TIPTAP,
        contentFormat: MessageContentFormatEnum.HTML,
        categories: [],
        language: PartnerModule.partner.language || LanguageCodeEnum.DE
      })
    });
    this.isPlain =
      this.template.meta?.editor === TemplateEditorEnum.DEFAULT &&
      this.template.meta?.contentFormat === MessageContentFormatEnum.PLAIN;
    this.editorMode = TemplateBodyEditorModeEnum.RICH;
    this.isKeyInputRequested = !!this.template.key;
    this.isFullscreen = this.fullscreen ?? false;

    this.isDialogActive = true;
    this.$nextTick(() => {
      (this.$refs.startField as HTMLInputElement)?.focus();
    });
  }

  close() {
    this.isDialogActive = false;
  }

  /**
   * The backend lowercases every key it receives (`convertToKey`), so the input does the same:
   * the key that is typed is the key that is created.
   */
  onKeyInput(value: string) {
    this.template.key = (value ?? "").toLowerCase();
  }

  toggleKeyInput() {
    this.isKeyInputRequested = !this.isKeyInputRequested;

    // do not submit a key that is no longer visible
    if (!this.isKeyInputRequested) this.template.key = "";
  }

  onContentFormatChange(isPlain: boolean) {
    this.isPlain = isPlain;

    // the html source editor is not applicable to plain text templates
    if (isPlain) this.editorMode = TemplateBodyEditorModeEnum.RICH;
  }

  async onRightClick() {
    try {
      this.isLoading = true;
      this.isDialogActive = true;

      if (this.isPlain) {
        this.template.meta.contentFormat = MessageContentFormatEnum.PLAIN;
        this.template.meta.editor = TemplateEditorEnum.DEFAULT;
      } else if (this.isHtmlSource) {
        // the body was not written with the tiptap editor but pasted as html
        this.template.meta.contentFormat = MessageContentFormatEnum.HTML;
        this.template.meta.editor = TemplateEditorEnum.DEFAULT;
      } else {
        this.template.meta.contentFormat = MessageContentFormatEnum.HTML;
        this.template.meta.editor = TemplateEditorEnum.TIPTAP;
      }

      await this.template.create();
      this.$emit("created", this.template);
      this.close();
    } catch (e) {
      handleError(e);
    } finally {
      this.isLoading = false;
    }
  }
}
</script>
